import React, { useState } from 'react';
import {
  AudioLines,
  Check,
  ChevronDown,
  Copy,
  ListChecks,
  PhoneIncoming,
  PhoneOutgoing,
  RefreshCw,
} from 'lucide-react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';

import { cn } from '../../../lib/utils';
import {
  useLeadTranscripts,
  useRefreshLeadTranscripts,
} from '../../../services/leads';

// Call transcripts on the LEAD (Nic 2026-08-18): the RingSense sweep files
// recorded calls against the lead; this card shows them on the board
// slide-over AND the Handoffs workspace so nobody copy-pastes from
// RingCentral again. `endpointBase` picks the calling surface's own gated
// routes ('/onboarding-board/leads' today; Lead Handoffs is retired).
//
// Latency honesty: RingSense takes minutes to ~1 hour after a call. "Check
// now" pulls whatever is ready and reports the rest as still transcribing —
// it removes the sweep wait, never the AI wait.
//
// Each call is its own tile (Nic 2026-10-11: "hard to make out the
// different areas"): a header (direction, when, how long, tone), the
// summary, the next steps in their own amber box, and the transcript as a
// conversation, each speaker in their own color.

const whenLabel = (d) =>
  d
    ? new Date(d).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      })
    : '';

const durationLabel = (secs) => {
  if (secs == null) return null;
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m ? `${m}m ${s}s` : `${s}s`;
};

const SENTIMENT_TONE = {
  Positive: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  Negative: 'bg-rose-500/10 text-rose-700 dark:text-rose-300',
};

// The transcript as turns: a speaker's lines run together, the speaker in
// bold and in their own color. RingSense lines read "*Name:* text"; older
// ones "Name: text".
const SPEAKER = /^\*([^*\n]{1,60}):\*\s?(.*)$|^([^:*\n]{1,40}):\s(.*)$/;
const SPEAKER_TONE = [
  { name: 'text-sky-700 dark:text-sky-300', bar: 'border-sky-300 dark:border-sky-400/40' },
  { name: 'text-violet-700 dark:text-violet-300', bar: 'border-violet-300 dark:border-violet-400/40' },
  { name: 'text-teal-700 dark:text-teal-300', bar: 'border-teal-300 dark:border-teal-400/40' },
  { name: 'text-amber-700 dark:text-amber-300', bar: 'border-amber-300 dark:border-amber-400/40' },
];
const turnsOf = (raw) => {
  const turns = [];
  raw.split('\n').forEach((line) => {
    const m = line.match(SPEAKER);
    const who = m ? (m[1] || m[3]).trim() : null;
    const text = m ? m[2] ?? m[4] : line.replace(/\*/g, '');
    if (!text.trim() && !who) return;
    const last = turns[turns.length - 1];
    if (last && (who === null || who === last.who)) last.lines.push(text);
    else turns.push({ who: who || '', lines: [text] });
  });
  return turns;
};

const Conversation = ({ raw }) => {
  const turns = turnsOf(raw);
  const toneBy = new Map();
  return (
    <div className="space-y-2.5">
      {turns.map((turn, i) => {
        if (!toneBy.has(turn.who)) toneBy.set(turn.who, SPEAKER_TONE[toneBy.size % SPEAKER_TONE.length]);
        const tone = toneBy.get(turn.who);
        return (
          // eslint-disable-next-line react/no-array-index-key
          <div key={i} className={cn('border-l-2 pl-2.5', tone.bar)}>
            {turn.who ? (
              <div className={cn('text-[11px] font-bold', tone.name)}>{turn.who}</div>
            ) : null}
            <div className="whitespace-pre-wrap text-[12px] leading-relaxed text-slate-700 dark:text-slate-200">
              {turn.lines.join('\n')}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const TranscriptRow = ({ t }) => {
  const [expanded, setExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const inbound = t.direction === 'inbound';
  const DirIcon = inbound ? PhoneIncoming : PhoneOutgoing;
  // The copy is plain text: the speaker markers' asterisks come out.
  const text = (t.transcriptRawText || '').replace(/\*/g, '');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Could not copy. Select the text instead.');
    }
  };
  const duration = durationLabel(t.durationSeconds);
  return (
    <div className="overflow-hidden rounded-xl bg-white ring-1 ring-slate-200/80 shadow-[0_1px_2px_rgba(15,23,42,0.04)] dark:!bg-white/[0.03] dark:ring-white/10">
      <div className="flex items-center gap-2.5 px-3 pb-2 pt-2.5">
        <span
          className={cn(
            'flex h-7 w-7 shrink-0 items-center justify-center rounded-full',
            inbound
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-300'
              : 'bg-sky-500/10 text-sky-600 dark:text-sky-300',
          )}
        >
          <DirIcon size={13} strokeWidth={2.25} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="truncate text-[13px] font-semibold text-slate-800 dark:text-slate-100">
            {whenLabel(t.meetingDatetime)}
          </div>
          <div className="text-[11px] text-slate-400 dark:text-slate-500">
            {inbound ? 'They called' : 'We called'}
            {duration ? ` · ${duration}` : ''}
          </div>
        </div>
        {t.sentiment ? (
          <span
            title="RingSense's read of the call's tone"
            className={cn(
              'shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-semibold',
              SENTIMENT_TONE[t.sentiment] || 'bg-slate-100 text-slate-500 dark:!bg-white/10 dark:text-slate-400',
            )}
          >
            {t.sentiment}
          </span>
        ) : null}
      </div>
      {/* The FULL summary, never ellipsized (Nic 2026-08-18, same rule as
          the KPI tiles): RingSense digests are a sentence or two and are
          the whole value of the closed tile. */}
      {t.summary ? (
        <p className="mb-0 px-3 text-[12px] leading-relaxed text-slate-600 dark:text-slate-300">{t.summary}</p>
      ) : null}
      {/* RingSense's NextSteps: the operational payoff ("Josue will call
          Jennifer tomorrow at 9 a.m."), amber so it reads as a to-do. */}
      {t.nextSteps?.length ? (
        <div className="mx-3 mt-2 rounded-lg bg-amber-500/[0.08] px-2.5 py-2">
          <div className="mb-1 flex items-center gap-1 text-[10.5px] font-bold uppercase tracking-wide text-amber-700 dark:text-amber-300">
            <ListChecks size={12} /> Next steps
          </div>
          <ul className="mb-0 list-disc space-y-0.5 pl-4 text-[12px] leading-snug text-amber-900 dark:text-amber-100">
            {t.nextSteps.map((step, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={i}>{step}</li>
            ))}
          </ul>
        </div>
      ) : null}
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        className="mt-2.5 flex w-full items-center justify-between gap-2 border-t border-slate-100 px-3 py-2 text-[12px] font-semibold text-slate-500 outline-none hover:bg-slate-50 hover:text-slate-700 focus:outline-none dark:border-white/10 dark:text-slate-400 dark:hover:!bg-white/[0.04] dark:hover:text-slate-200"
      >
        <span className="inline-flex items-center gap-1.5">
          <AudioLines size={13} /> {expanded ? 'Hide transcript' : 'Read transcript'}
        </span>
        <ChevronDown size={14} className={cn('transition-transform', expanded && 'rotate-180')} />
      </button>
      {expanded ? (
        <div className="border-t border-slate-100 bg-slate-50/70 px-3 py-2.5 dark:border-white/10 dark:!bg-black/10">
          <div className="os-scroll max-h-80 overflow-y-auto pr-1">
            {t.transcriptRawText ? (
              <Conversation raw={t.transcriptRawText} />
            ) : (
              <span className="text-[12px] text-slate-400">No transcript text.</span>
            )}
          </div>
          {t.transcriptRawText ? (
            <button
              type="button"
              onClick={copy}
              className="mt-2.5 inline-flex items-center gap-1 rounded-full bg-white px-2.5 py-1 text-[11px] font-semibold text-slate-600 ring-1 ring-slate-200 hover:bg-slate-100 dark:!bg-white/10 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/15"
            >
              {copied ? <Check size={11} /> : <Copy size={11} />}
              {copied ? 'Copied' : 'Copy transcript'}
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};

// `bare`: the surrounding section already says "Call transcripts", so only
// the Check now button stays above the list. The Client Workspace passes
// endpointBase '/client-onboarding' and its onboardingId as `leadId`: the
// same routes' shape, for a client (Nic 2026-10-11).
const LeadTranscriptsCard = ({ leadId, endpointBase, bare = false }) => {
  const queryClient = useQueryClient();
  const list = useLeadTranscripts(endpointBase, leadId);
  const refresh = useRefreshLeadTranscripts(
    endpointBase,
    (data) => {
      queryClient.invalidateQueries({ queryKey: ['lead-transcripts', leadId] });
      if (data?.pendingCount) {
        toast.info(
          `${data.pendingCount} call${data.pendingCount === 1 ? ' is' : 's are'} still transcribing at RingCentral. Check back in a bit.`,
        );
      } else if (!data?.inserted) {
        toast.success('Up to date. No new transcripts yet.');
      }
    },
    {
      onError: (err) =>
        toast.error(
          err?.response?.data?.msg || 'Could not check RingCentral.',
        ),
    },
  );
  const rows = list.data?.transcripts || [];

  return (
    <div className={bare ? '' : 'mb-4'}>
      <div className="mb-2 flex items-center justify-between gap-2">
        {bare ? (
          <span className="text-[11px] text-slate-400 dark:text-slate-500">
            {rows.length ? `${rows.length} call${rows.length === 1 ? '' : 's'}, newest first` : null}
          </span>
        ) : (
          <span className="flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">
            <AudioLines size={12} /> Call transcripts
            {rows.length ? (
              <span className="rounded-full bg-slate-100 px-1.5 py-px text-[10px] font-semibold normal-case tracking-normal text-slate-500 dark:!bg-white/10 dark:text-slate-400">
                {rows.length}
              </span>
            ) : null}
          </span>
        )}
        <button
          type="button"
          disabled={refresh.isLoading}
          onClick={() => refresh.mutate({ leadId })}
          title="Pull their recent recorded calls from RingCentral now instead of waiting for the sweep. Transcripts still take RingCentral minutes to an hour to generate after a call."
          className="inline-flex shrink-0 items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[11.5px] font-semibold text-slate-600 ring-1 ring-slate-200 shadow-[0_1px_2px_rgba(15,23,42,0.05)] outline-none hover:bg-slate-50 hover:text-slate-800 focus:outline-none disabled:opacity-50 dark:!bg-white/5 dark:text-slate-300 dark:ring-white/10 dark:hover:!bg-white/10"
        >
          <RefreshCw size={12} className={refresh.isLoading ? 'animate-spin' : ''} />
          {refresh.isLoading ? 'Checking…' : 'Check now'}
        </button>
      </div>
      {list.isLoading ? (
        <div className="space-y-2">
          <div className="h-20 animate-pulse rounded-xl bg-slate-100 dark:!bg-white/5" />
          <div className="h-20 animate-pulse rounded-xl bg-slate-100 dark:!bg-white/5" />
        </div>
      ) : rows.length === 0 ? (
        <div className="flex flex-col items-center rounded-xl border-2 border-dashed border-slate-200 px-4 py-6 text-center dark:border-white/10">
          <span className="mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-400 dark:!bg-white/5">
            <AudioLines size={16} />
          </span>
          <div className="text-[12.5px] font-semibold text-slate-600 dark:text-slate-300">No transcripts yet</div>
          <div className="mt-0.5 text-[11.5px] leading-relaxed text-slate-400">
            Recorded calls land here on their own once RingCentral finishes transcribing.
          </div>
        </div>
      ) : (
        <div className="space-y-2.5">
          {rows.map((t) => (
            <TranscriptRow key={t.transcriptId} t={t} />
          ))}
        </div>
      )}
    </div>
  );
};

export default LeadTranscriptsCard;
